import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import { useIntl } from 'react-intl';
import { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';

import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';

import { SwiftLldForm } from 'components/transfer/swift';
import { DynoAlertModal, DynoFormModal } from 'components/dyno-template/modals';

import { get } from 'utils/lodash';
import { handleRemoveValueSpace, isCutoffCountdownDisplay } from 'utils/transaction';
import { isLldRequired } from 'utils/transfer/swift/validation';
import { formatSwiftConfirmationBody } from 'utils/transfer/swift/formatter';
import { getSwiftConfirmation, putSwiftLldDetail } from 'middleware/actions/transfer';
import { getBeneficiaryBankDetail, getLldDynamicFields } from 'utils/transfer/swift/getter';
import { dynamicFieldHandler, handleFormSubmit, notesHandler, formTitleHandler, formFieldHandler } from 'utils/transfer/handler';

import { SWIFT_TRANSFER, TRANSFER_NOW } from 'settings/constants/transaction';
import { MODAL_FULLSCREEN_SIZE } from 'settings/constants/ui-control';
// import { getLLDTitle } from 'utils/transfer/getter';

// main container that hosts some Transfer type modals (Own Account, Intrabank)
// dynamic DynoFormModal is used for RTGS for now.
const TransferModal = ({
  userId,
  duration,
  onOpen,
  onError,
  onClose,
  onChange,
  formDetail,
  isFormOpen,
  transferType,
  senderAccount,
  transferDetail,
  beneficiaryBank,
  initialTransferDate,
  isFavouriteTransfer,
  initialSenderAccounts,
  isTransferNowBlocked,
  swiftBankDetail,
}) => {
  const { swiftLldDetail } = useSelector(state => ({ swiftLldDetail: state.TransferReducer.swiftLldDetail }), shallowEqual);

  const dispatch = useDispatch();
  const { formatMessage } = useIntl();

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down(MODAL_FULLSCREEN_SIZE));

  const [isNoticeOpen, setNoticeOpen] = useState(false);
  const [isLldOpen, setLldOpen] = useState(false);
  // const [lldTitle, setLLDTitle] = useState('');

  const transferMode = get(formDetail, 'transferMode', TRANSFER_NOW);
  // const isLLD = get(formDetail, 'isLLD', false);

  const bankName = get(beneficiaryBank, 'label', '');
  const swiftBeneficiaryBank = getBeneficiaryBankDetail(swiftBankDetail);

  const formFields = formFieldHandler(transferType);
  const formTitle = formTitleHandler(transferType);

  const serviceInfo = get(transferDetail, 'serviceInfoBean', {});
  const notes = notesHandler(serviceInfo, transferType, transferMode);

  const dynamicFields = dynamicFieldHandler({
    transferType,
    senderAccount,
    formDetail,
    transferDetail,
    swiftBankDetail,
    isTransferNowBlocked,
    initialTransferDate,
    isFavouriteTransfer,
  });

  const lldPurpose = get(formDetail, 'lldPurpose', {});
  const lldDynamicFields = useMemo(() => getLldDynamicFields(lldPurpose, formatMessage), [lldPurpose]);

  const submitData = {
    ...formDetail,
    userId,
    senderAccount,
    initialSenderAccounts,
    transferDetail,
    bankCode: get(beneficiaryBank, 'value', '') || get(formDetail, 'bankCode', ''),
    dataMappings: get(transferDetail, 'dataMappings', {}),
    allBank: get(transferDetail, 'allBank', {}),
  };

  const handleLldSubmit = () => {
    setLldOpen(false);

    if (isCutoffCountdownDisplay(transferType, transferMode, duration)) {
      setNoticeOpen(true);
      return;
    }

    // if (transferType === SWIFT_TRANSFER) {
    // reset LLD response to fix LLD form opening onMount
    dispatch(putSwiftLldDetail({}));
    dispatch(getSwiftConfirmation(formatSwiftConfirmationBody(submitData)));
    // } else if (transferType === OWN_ACCOUNT) {
    //   // handleFormSubmit(transferType, submitData);
    //   console.log('submitData:', submitData);
    // }
  };

  const handleNoticeClose = () => {
    setNoticeOpen(false);

    if (transferType === SWIFT_TRANSFER) {
      dispatch(getSwiftConfirmation(formatSwiftConfirmationBody(submitData)));
      return;
    }

    handleFormSubmit(transferType, submitData);
  };

  const handleNoticeOpen = () => {
    onClose(false);
    setNoticeOpen(true);
  };

  const handleLldClose = () => {
    // when user close LLD form manually, open back the main transfer form
    onOpen();
    setLldOpen(false);
  };

  const handleSubmit = () => {
    if (transferType !== SWIFT_TRANSFER && isCutoffCountdownDisplay(transferType, transferMode, duration)) {
      handleNoticeOpen();
      return;
    }

    // if (isLLD) {
    //   setLldOpen(true);
    //   setLLDTitle(getLLDTitle(transferType));
    //   return;
    // }

    handleFormSubmit(transferType, submitData);
  };

  // handle input acc number with pattern #### #### #### #### ###
  const inputAccNumber = document.getElementById('accountNumber');
  if (inputAccNumber !== null) {
    inputAccNumber.addEventListener('mousedown', handleRemoveValueSpace(inputAccNumber));
  }

  useEffect(() => {
    if (!isEmpty(beneficiaryBank)) {
      onChange({ beneficiaryBankLabel: bankName });
    }
  }, [beneficiaryBank]);

  // SWIFT transfer bank detail flow is different. so cannot use exisiting beneficiaryBank set.
  useEffect(() => {
    if (!isEmpty(swiftBankDetail)) {
      onChange(swiftBeneficiaryBank);
    }
  }, [swiftBankDetail]);

  useEffect(() => {
    if (!isEmpty(lldPurpose)) {
      // if purpose changes, reset the description field
      onChange({ lldDescription: {} });
    }
  }, [lldPurpose]);

  useEffect(() => {
    if (!isEmpty(swiftLldDetail)) {
      const isLldDisplay = isLldRequired(swiftLldDetail);
      setLldOpen(isLldDisplay);

      if (isLldDisplay) {
        // setLLDTitle(getLLDTitle(transferType));
        onClose(false);
        return;
      }
      // if LLD not required, submit the form
      handleLldSubmit();
      return;
    }

    setLldOpen(false);
  }, [swiftLldDetail]);

  return (
    <>
      {!isLldOpen && (
        <DynoFormModal
          fullScreen={fullScreen}
          onSubmit={handleSubmit}
          isOpen={isFormOpen}
          onClose={onClose}
          title={formTitle}
          formDetail={formDetail}
          onChange={onChange}
          fields={formFields}
          dynamicFields={dynamicFields}
          notes={notes}
          hasNoteContent
          className={transferType === SWIFT_TRANSFER ? `swift-transfer` : ``}
        />
      )}

      {transferType === SWIFT_TRANSFER && (
        <SwiftLldForm
          isOpen={isLldOpen}
          onChange={onChange}
          onClose={handleLldClose}
          onSubmit={handleLldSubmit}
          formDetail={formDetail}
          dynamicFields={lldDynamicFields}
          // title={lldTitle}
        />
      )}

      <DynoAlertModal
        isOpen={isNoticeOpen}
        onClose={handleNoticeClose}
        title={formatMessage({ id: 'transaction.cutoffAlert.title' })}
        description={formatMessage({ id: 'transaction.cutoffAlert.countdownDescription' }, { timer: duration })}
      />
    </>
  );
};

TransferModal.propTypes = {
  onOpen: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  senderAccount: PropTypes.object.isRequired,
  initialSenderAccounts: PropTypes.array.isRequired,
  beneficiaryBank: PropTypes.object.isRequired,
  isFormOpen: PropTypes.bool.isRequired,
  transferType: PropTypes.string.isRequired,
  transferDetail: PropTypes.object.isRequired,
  onError: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  formDetail: PropTypes.object.isRequired,
  duration: PropTypes.number.isRequired,
  isTransferNowBlocked: PropTypes.bool.isRequired,
  swiftBankDetail: PropTypes.object.isRequired,
  isFavouriteTransfer: PropTypes.bool.isRequired,
  initialTransferDate: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export default TransferModal;
